// Telnyx calling regions, for teammates comparing them by hand (Settings → Audio devices).
//
// The app never picks or switches the region itself. It records the real call delay (the "· 84 ms" on the call
// screen) per region on this device, and can run a quick connection test to each region, so the teammate can
// see which works best and choose it.

export const AUTO_REGIONS = ["us-west", "us-central", "us-east", "ca-central", "eu"] as const;
export const REGION_NAMES: Record<string, string> = {
  "": "Telnyx default",
  "us-west": "US West (California)",
  "us-central": "US Central (Chicago)",
  "us-east": "US East (Virginia / New York)",
  "ca-central": "Canada (ca-central)",
  eu: "Europe",
};

/** Call delay samples per region kept (about 1–2 minutes of calls), and how long they count. */
const MAX_SAMPLES = 60;
const MIN_SAMPLES = 3;
const SAMPLE_AGE = 14 * 24 * 3600_000;

const TEST_KEY = "joy.region.auto.v1";
const RTT_KEY = "joy.region.rtt.v2";

export interface RegionResult {
  /** "" = Telnyx default host (rtc.telnyx.com). */
  region: string;
  ms: number | null;
}
interface SavedTest {
  at: number;
  results: RegionResult[];
}
type RttLog = Record<string, { s: number[]; at: number }>;

function read<T>(key: string): T | null {
  try {
    return JSON.parse(localStorage.getItem(key) ?? "null") as T | null;
  } catch {
    return null;
  }
}
function write(key: string, v: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    // private mode: this session only
  }
}

const known = (r: string) => r === "" || (AUTO_REGIONS as readonly string[]).includes(r);

/** The last connection test (for the settings screen), or null. */
export function lastRegionTest(): SavedTest | null {
  const t = read<SavedTest>(TEST_KEY);
  return t && Array.isArray(t.results) && typeof t.at === "number" ? t : null;
}

// ------------------------------------------------------------------ real call delay

/** Records one call-delay reading (ms) for the region the call used. */
export function recordCallRtt(region: string, ms: number) {
  if (!known(region) || !Number.isFinite(ms) || ms <= 0 || ms > 2000) return;
  const log = read<RttLog>(RTT_KEY) ?? {};
  const e = log[region] ?? { s: [], at: 0 };
  e.s = [...e.s, Math.round(ms)].slice(-MAX_SAMPLES);
  e.at = Date.now();
  log[region] = e;
  write(RTT_KEY, log);
}

/** Median call delay per region from this device's recent calls (regions with enough readings only). */
export function callDelayByRegion(): { region: string; ms: number; n: number }[] {
  const log = read<RttLog>(RTT_KEY) ?? {};
  const out: { region: string; ms: number; n: number }[] = [];
  for (const [region, e] of Object.entries(log)) {
    if (!known(region) || !e || !Array.isArray(e.s) || e.s.length < MIN_SAMPLES || Date.now() - e.at > SAMPLE_AGE) continue;
    const s = [...e.s].sort((a, b) => a - b);
    out.push({ region, ms: s[Math.floor(s.length / 2)], n: s.length });
  }
  return out.sort((a, b) => a.ms - b.ms);
}

// ------------------------------------------------------------------ connection test

/** Time to open a WebSocket to one regional server, or null if it failed / took over `timeoutMs`. */
function probe(region: string, timeoutMs = 3000): Promise<number | null> {
  return new Promise((resolve) => {
    let ws: WebSocket | null = null;
    let done = false;
    const t0 = performance.now();
    const finish = (v: number | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        ws?.close();
      } catch {
        // already closed
      }
      resolve(v);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    try {
      ws = new WebSocket(`wss://${region ? `${region}.` : ""}rtc.telnyx.com`);
      ws.onopen = () => finish(Math.round(performance.now() - t0));
      ws.onerror = () => finish(null);
    } catch {
      finish(null);
    }
  });
}

let running: Promise<RegionResult[]> | null = null;

/** Tests every region (two rounds, keeps each one's best). Sorted fastest first; no answer = ms null, last. */
export function measureRegions(): Promise<RegionResult[]> {
  running ??= (async () => {
    const candidates = ["", ...AUTO_REGIONS];
    const best = new Map<string, number | null>(candidates.map((r) => [r, null]));
    for (let round = 0; round < 2; round++) {
      const times = await Promise.all(candidates.map((r) => probe(r)));
      candidates.forEach((r, i) => {
        const t = times[i];
        const prev = best.get(r) ?? null;
        if (t != null && (prev == null || t < prev)) best.set(r, t);
      });
    }
    const results: RegionResult[] = candidates
      .map((region) => ({ region, ms: best.get(region) ?? null }))
      .sort((a, b) => (a.ms ?? Infinity) - (b.ms ?? Infinity));
    if (results.some((r) => r.ms != null)) write(TEST_KEY, { at: Date.now(), results } satisfies SavedTest);
    return results;
  })().finally(() => {
    running = null;
  });
  return running;
}


// ------------------------------------------------------------------ speed test (real test calls)
//
// Settings › Call speed: the app places short silent test calls (the server answers them, nobody rings) through
// each calling region and, for admins, each media site, and measures the real round trip of call audio. The
// fastest measured combination is kept as this device's "best", calls connect through it automatically, and its
// delay is the yardstick: when real calls get clearly slower than it, the app measures again.

export interface SpeedResult {
  region: string;
  /** Media site in use during this measurement ("Latency" = Telnyx picks; null = unknown). */
  site: string | null;
  /** Median round trip, ms (null = the test call didn't connect). */
  ms: number | null;
  /** Spread between readings (p80 − p20), ms: how steady the path is. */
  spread: number | null;
  samples: number;
  /** Telnyx media server address the audio went to. */
  ip: string | null;
  relay: string | null;
  error?: string;
}
export interface SpeedRun {
  at: number;
  results: SpeedResult[];
  best: { region: string; site: string | null; ms: number } | null;
}

const RUN_KEY = "joy.speed.run.v1";
const RECENT_KEY = "joy.speed.recent.v1";

/**
 * The test call only measures this phone → the Telnyx site. A real call then goes on from that site to the customer,
 * and Joy Travel's customers are on US / Canada numbers. For a site in North America that onward hop is short and
 * about the same for every site; for a site overseas it's an ocean crossing the test can't see. Without this, a
 * teammate in the Philippines measured Sydney as "fastest" (closest to him) although every call to a US customer
 * then went Manila → Sydney → USA, a detour of about 6,000 km (~60–70 ms more round trip than straight across).
 * Estimate: great-circle distance from the site to the nearest US site, ×1.25 for cable routes, at the speed of
 * light in fibre (~100 km of distance per ms of round trip).
 */
const OVERSEAS_SITES: Record<string, [number, number]> = {
  "London, UK": [51.51, -0.13],
  "Amsterdam, Netherlands": [52.37, 4.9],
  "Frankfurt, Germany": [50.11, 8.68],
  "Sydney, Australia": [-33.87, 151.21],
};
const US_SITES: [number, number][] = [[37.34, -121.89], [41.88, -87.63], [39.04, -77.49]];
function km([a, b]: [number, number], [c, d]: [number, number]) {
  const r = Math.PI / 180;
  const h = Math.sin(((c - a) * r) / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin(((d - b) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}
/** Extra round trip (ms) from a media site to customers on North American numbers. */
export function onwardMs(site: string | null): number {
  const at = site ? OVERSEAS_SITES[site] : undefined;
  if (!at) return 0;
  return Math.round((Math.min(...US_SITES.map((u) => km(at, u))) * 1.25) / 100);
}

/** Score: median plus half the spread (a steady path beats a slightly faster jumpy one), plus the onward trip to the customer. */
export const speedScore = (r: SpeedResult) => (r.ms == null ? Infinity : r.ms + (r.spread ?? 0) / 2 + onwardMs(r.site));

/** Picks the winner, keeping the current one unless the other is clearly faster (5 ms and 10%). */
export function pickBest(results: SpeedResult[], current?: { region: string; site: string | null }): SpeedResult | null {
  const ok = results.filter((r) => r.ms != null).sort((a, b) => speedScore(a) - speedScore(b));
  const top = ok[0];
  if (!top) return null;
  const cur = current && ok.find((r) => r.region === current.region && (current.site == null || r.site === current.site));
  if (cur && cur !== top && speedScore(cur) - speedScore(top) < Math.max(5, speedScore(cur) * 0.1)) return cur;
  return top;
}

export function lastSpeedRun(): SpeedRun | null {
  const r = read<SpeedRun>(RUN_KEY);
  return r && typeof r.at === "number" && Array.isArray(r.results) ? r : null;
}
export function saveSpeedRun(run: SpeedRun) {
  write(RUN_KEY, run);
  write(RECENT_KEY, []); // fresh yardstick: only calls after this test count against it
}

/** The calling region calls use on this device: the measured best when automatic, else the one picked by hand. */
export function effectiveRegion(p: { region: string; regionAuto: boolean }): string {
  if (!p.regionAuto) return p.region;
  const best = lastSpeedRun()?.best?.region;
  if (best != null && known(best)) return best;
  return p.region;
}

/** One reading per call (its typical delay), for the "calls got slower than the best" check. */
export function recordCallDelay(region: string, ms: number) {
  if (!Number.isFinite(ms) || ms <= 0 || ms > 3000) return;
  const list = (read<{ at: number; region: string; ms: number }[]>(RECENT_KEY) ?? []).filter((x) => x && typeof x.ms === "number");
  write(RECENT_KEY, [...list, { at: Date.now(), region, ms: Math.round(ms) }].slice(-20));
}

/** How long a measurement stays trusted, and how much slower real calls may get before measuring again. */
export const RETEST_AFTER_MS = 7 * 24 * 3600_000;
export const slowerThanBest = (best: number, calls: number) => calls > best * 1.5 + 30;

/**
 * Why the speed should be measured again now (or null): never measured, measured over a week ago, or the last
 * three calls through the best region were all clearly slower than what the test measured.
 */
export function retestReason(): string | null {
  const run = lastSpeedRun();
  if (!run?.best) return "not measured yet";
  if (Date.now() - run.at > RETEST_AFTER_MS) return "last measured over a week ago";
  const calls = (read<{ at: number; region: string; ms: number }[]>(RECENT_KEY) ?? []).filter((c) => c.at > run.at && c.region === run.best!.region).slice(-3);
  if (calls.length === 3 && calls.every((c) => slowerThanBest(run.best!.ms, c.ms))) return `calls are slower than the best measured (${run.best.ms} ms)`;
  return null;
}

/** Median and spread (p80 − p20) of readings. */
export function summarize(samples: number[]): { ms: number | null; spread: number | null } {
  if (!samples.length) return { ms: null, spread: null };
  const s = [...samples].sort((a, b) => a - b);
  const at = (q: number) => s[Math.min(s.length - 1, Math.floor(q * s.length))];
  return { ms: Math.round(at(0.5)), spread: Math.round(at(0.8) - at(0.2)) };
}
